/* eslint-disable @typescript-eslint/naming-convention */
import { Component, ElementRef, Inject, Input, OnInit, ViewChild, OnDestroy, EventEmitter } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { FormBuilderService } from 'app/core/services/form-builder/form-builder.service';
import { SDKFormSerializer } from 'app/core/services/sdk_service/form-serializer';
import { ZuperSdkService } from 'app/core/services/sdk_service/sdk.services';
import { invert } from 'lodash';
import { Subscription } from 'rxjs';
import { FormFieldInterface } from '../form-builder/form-builder.interface';

interface SDKDialogData {
  label: string;
  value: string | any[];
  type: 'SINGLELINE' | 'MULTILINE' | 'DATE' | 'TIME' | 'DATETIME' | 'FILE' | 'MULTIFILE';
  width: string;
}

interface SDKDialogOption {
  title: string;
  position: 'center' | 'right';
  dataType: 'DETAILS' | 'IFRAME' | 'FORM';
  size: 'sm' | 'md' | 'lg' | 'xl';
  url?: string;
  data: SDKDialogData[] | SDKDialogData;
  buttons: any;
  fields?: any[];
  auto_close?: boolean;
  hide_footer?: boolean;
  hide_header?: boolean;
  full_screen?: boolean;
  height?: string;
  width?: string;
}

@Component({
  selector: 'app-sdk-custom-dialog',
  templateUrl: './sdk-custom-dialog.component.html',
  styleUrls: ['./sdk-custom-dialog.component.scss']
})
export class SdkCustomDialogComponent implements OnInit, OnDestroy {

  defaultOption: Partial<SDKDialogOption> = {
    position: 'center',
    auto_close: true,
    hide_footer: false,
    hide_header: false,
    full_screen: false,
    size: 'md',
  };
  formConfig: SDKFormSerializer;
  formValues: any;

  $subscription: Subscription[] = [];

  iframeEventEmitter = new EventEmitter();

  // emit event when auto close set to false in options
  confirmEventEmitter = new EventEmitter();

  sizeOptions: any = {
    'sm': ['20vw', '30vh'],
    'md': ['40vw', '45vh'],
    'lg': ['60vw', '60vh'],
    'xl': ['80vw', '75vh'],
    'fullScreen': ['100vw', '100vh']
  };

  sdkDataIframeUrl: any;
  size: any;
  formGroup: FormGroup;
  currentFormData: any;
  private _sdkDataFrame: ElementRef<HTMLIFrameElement>;

  @ViewChild('sdkDialogFrame')
  set sdkDataFrame(frame: ElementRef<HTMLIFrameElement>) {
    if (frame) {
      this._sdkDataFrame = frame;
      window.addEventListener('message', (event) => {
        if (this.data.url.includes(event.origin)) {
          this.iframeEventEmitter.emit(event.data);
        }
      });
      
    }
  }

  constructor(@Inject(MAT_DIALOG_DATA) public data: SDKDialogOption,
    public dialogRef: MatDialogRef<SdkCustomDialogComponent>,
    private _sanitizer: DomSanitizer,
    private _formBuilderService: FormBuilderService,
    private _zuperSdkService: ZuperSdkService,
  ) {
    this.data = {
      ...this.defaultOption,
      ...this.data,
    };

    // selecting dialog size based on priorities and config option availability
    if(this.data.full_screen){
      this.size = this.sizeOptions['fullScreen'];
    }else if(this.data.height && this.data.width){
      this.size = [this.data.width, this.data.height];
    }else{
      this.size = this.sizeOptions[this.data.size || 'md'];
    }
    if (this.data.dataType === 'IFRAME' && this.data.url) {
      this.sdkDataIframeUrl = this._sanitizer.bypassSecurityTrustResourceUrl(this.data?.url);
    } else if (this.data.dataType === 'FORM' && this.data.fields) {
      this.formConfig = new SDKFormSerializer(this.data.fields);
      this.formGroup = this._formBuilderService.constructFormGroupFromConfig(this.formConfig.fields);
    }
    const $subFV = this._zuperSdkService.onFormValueUpdatesAsRequest.subscribe((patchValue: any) => {
      if (patchValue && this.formGroup) {
        const invertedFieldMap = invert(this.formConfig.fieldMap);
        const patchData = {};
        Object.keys(patchValue).forEach((item: string) => {
          if (invertedFieldMap[item]) {
            patchData[invertedFieldMap[item]] = patchValue[item];
          }
        });
        this.formValues = patchData;
      }
    });
    const $subFC = this._zuperSdkService.onFormConfigUpdateAsRequest.subscribe((patchConfig: any) => {
      if (patchConfig && this.formGroup) {
        Object.keys(patchConfig).forEach((item: any) => {
          const fieldIndex = this.data.fields.findIndex(i => i.id === item);
          if (fieldIndex >= 0) {
            this.data.fields[fieldIndex] = {
              ...this.data.fields[fieldIndex],
              ...patchConfig[item],
              // eslint-disable-next-line @typescript-eslint/naming-convention
              field_options: {
                ...this.data.fields[fieldIndex]['field_options'],
                ...patchConfig[item]['field_options']
              }
            };
          }
          this.formConfig = new SDKFormSerializer(this.data.fields);
          this.formGroup = this._formBuilderService.constructFormGroupFromConfig(this.formConfig.fields);
          this.formConfig.fields.forEach((field: FormFieldInterface) => {
            if (field.show_custom_error) {
              this.formGroup.get(field.field_name)?.setErrors({'invalid': true});
              this.formGroup.get(field.field_name)?.markAsTouched();
            };
          });
        });
      }
    });
    const $subIframeDispatch = this._zuperSdkService.dispatchDataForIframeModal.subscribe((message: any) => {
      if (message) {
        this.sendMessageToChildIframe(message);
      }
    });
    this.$subscription = [$subFC, $subFV, $subIframeDispatch];
  }

  ngOnInit(): void { }

  sendMessageToChildIframe(message: any): void {
    const frameWindow = this._sdkDataFrame.nativeElement.contentWindow;
    const regex = /^(https?:\/\/[^\/]+)/;
    const match = this.data.url.match(regex);
    const origin = match ? match[0] : null;
    if (origin) {
      frameWindow?.postMessage(
        message,
        origin
      );
    }
  }

  closeDialog(type: 'close' | 'cancel' | 'confirm' = 'close', data: any = null): void {
    this.dialogRef.close({
      type,
      data
    });
  }

  onFormSubmit(formValue: any): void {
    if (formValue.checklist) {
      const data = this.formConfig.deSerializeCheckList(formValue.checklist);
      if (this.data.auto_close) {
        this.closeDialog('confirm', data);
      } else {
        this.confirmEventEmitter.emit(data);
      }
    } else {
      this.closeDialog('close');
    }
  }

  onFormChange(formValue: any): void {
    if (formValue) {
      const data = this.formConfig.deSerializeCheckList(formValue);
      if(JSON.stringify(data) !== JSON.stringify(this.currentFormData)){
        this._zuperSdkService.onFormDialogChanges.next(data);
        this.currentFormData = data;
      }
    }
  }

  ngOnDestroy(): void {
    this.$subscription.forEach($sub => $sub.unsubscribe());
  }

  onIframeLoad(event: any): void {
    this._zuperSdkService.onCustomDialogIframeLoad.next(true);
  }
}
